import React, { useState, useEffect } from "react";
import { BaseModal } from "../ui/BaseModal.tsx";
import AdminUser from "../user/AdminUser.tsx";
import CreationUser from "../user/CreationUser.tsx";
import { Box, Button, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Users, UserPlus } from "lucide-react";
import { CustomTextField } from "../ui/CustomTextField.tsx";
import { CustomAlert } from "../ui/CustomAlert.tsx";
import { getUserApi } from "../../services/userApi";

interface UserManagementModalProps {
  onClose: () => void;
  isOpen: boolean;
}

interface PasswordCriteria {
  LENGTH: string;
  NUMBER: string;
  LOWERCASE: string;
  UPPERCASE: string;
  SPECIAL_CHAR: string;
}

interface TextConfig {
  USER_MANAGEMENT_TEXT_TITLE: string;
  USER_MENU_TEXT_ADMIN: string;
  USER_MENU_TEXT_USER: string;
  CREATION_USER_TEXT_CLOSE: string;
  ADMIN_TOAST_USER_ERROR_EMPTY: string;
  ADMIN_TOAST_USER_ERROR_GET: string;
  CREATION_USER_TOAST_USER_CREATING: string;
  CREATION_USER_TOAST_USER_CREATED: string;
  CREATION_USER_TOAST_USER_ERROR: string;
  CREATION_USER_TEXT_USERNAME: string;
  CREATION_USER_TEXT_TEMP_PASSWORD: string;
  CREATION_USER_TEXT_EMAIL: string;
  CREATION_USER_TEXT_ROLE: string;
  CREATION_USER_TEXT_USER: string;
  CREATION_USER_TEXT_ADMIN: string;
  CREATION_USER_TEXT_CREATE: string;
  CREATION_USER_TEXT_PASSWORD_CRITERIA: PasswordCriteria;
  [key: string]: any;
}

const ToggleContainer = styled(Box)({
  width: "100%",
  backgroundColor: "#18181B",
  padding: "4px",
  borderRadius: "8px",
  display: "flex",
  gap: "4px",
  marginBottom: "16px",
  position: "relative",
});

interface SliderProps {
  $position: string;
}

const Slider = styled(Box)<SliderProps>(({ $position }) => ({
  position: "absolute",
  width: "calc(50% - 6px)",
  height: "calc(100% - 8px)",
  backgroundColor: "#22C55E",
  borderRadius: "6px",
  transition: "transform 0.3s ease-in-out",
  transform: $position === "left" ? "translateX(0)" : "translateX(100%)",
  left: "4px",
  top: "4px",
}));

const ToggleButton = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  flex: 1,
  padding: "8px",
  textAlign: "center",
  color: selected ? "#18181B" : "#FFFFFF",
  cursor: "pointer",
  borderRadius: "6px",
  position: "relative",
  zIndex: 1,
  transition: "color 0.2s ease-in-out",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  fontWeight: selected ? 600 : 400,
  "&:hover": {
    backgroundColor: !selected ? "rgba(255, 255, 255, 0.1)" : "transparent",
  },
}));

const ContentContainer = styled(Box)({
  backgroundColor: "#2D2D2D",
  borderRadius: "8px",
  padding: "20px",
  marginBottom: "16px",
});

export default function UserManagementModal({
  onClose,
  isOpen,
}: UserManagementModalProps) {
  const [activeTab, setActiveTab] = useState<"admin" | "create">("admin");
  const [textConfig, setTextConfig] = useState<TextConfig>({} as TextConfig);
  const [username, setUsername] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [email, setEmail] = useState("");
  const [group, setGroup] = useState("");
  const [alertConfig, setAlertConfig] = useState<{
    open: boolean;
    type: "loading" | "success" | "error" | "confirm";
    message: string;
    title?: string;
  }>({
    open: false,
    type: "loading",
    message: "",
    title: "",
  });

  useEffect(() => {
    const fetchTextConfig = async () => {
      try {
        const response = await fetch("/text.json");
        const data = await response.json();
        setTextConfig(data as TextConfig);
      } catch (error) {
        console.error("Error loading text config:", error);
      }
    };
    fetchTextConfig();
  }, []);

  const handleCloseAlert = () => {
    setAlertConfig((prev) => ({ ...prev, open: false }));
  };

  const handleSearch = async () => {
    if (!username.trim()) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.ADMIN_TOAST_USER_ERROR_EMPTY,
      });
      return;
    }

    setIsLoading(true);
    setUserData(null);
    try {
      const data = await getUserApi(username);
      setUserData(data);
      setEmail(data.attributes.email || "");
      setGroup(data.groups.includes("admin") ? "admin" : "user");
    } catch (error) {
      setAlertConfig({
        open: true,
        type: "error",
        message: textConfig.ADMIN_TOAST_USER_ERROR_GET,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <BaseModal
        title={textConfig.USER_MANAGEMENT_TEXT_TITLE}
        onClose={onClose}
        isOpen={isOpen}
      >
        <ToggleContainer>
          <Slider $position={activeTab === "admin" ? "left" : "right"} />
          <ToggleButton
            selected={activeTab === "admin"}
            onClick={() => setActiveTab("admin")}
          >
            <Users size={18} />
            {textConfig.USER_MENU_TEXT_ADMIN}
          </ToggleButton>
          <ToggleButton
            selected={activeTab === "create"}
            onClick={() => setActiveTab("create")}
          >
            <UserPlus size={18} />
            {textConfig.USER_MENU_TEXT_USER}
          </ToggleButton>
        </ToggleContainer>

        {activeTab === "admin" && (
          <>
            <Typography
              variant="body1"
              sx={{
                color: "#fff",
                mb: 1,
                fontWeight: 500,
              }}
            >
              Nombre del Usuario
            </Typography>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
                width: "100%",
              }}
            >
              <CustomTextField
                isFullWidth
                placeholder="Buscar usuario..."
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Box
                display="flex"
                justifyContent="flex-end"
                gap={2}
                sx={{
                  position: "relative",
                  bottom: 0,
                  width: "100%",
                }}
              >
                <Button
                  onClick={handleSearch}
                  disabled={isLoading}
                  sx={{
                    backgroundColor: "#22C55E",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#16A34A",
                    },
                    "&:disabled": {
                      backgroundColor: "#1F1F1F",
                      color: "#666666",
                    },
                    textTransform: "none",
                    minWidth: "100px",
                    borderRadius: "8px",
                    padding: "8px 16px",
                  }}
                >
                  {isLoading ? "Buscando..." : "Buscar"}
                </Button>
                <Button
                  onClick={onClose}
                  sx={{
                    backgroundColor: "#FF3D3D",
                    color: "#FFFFFF",
                    "&:hover": {
                      backgroundColor: "#FF5252",
                    },
                    textTransform: "none",
                    minWidth: "100px",
                    borderRadius: "8px",
                    padding: "8px 16px",
                  }}
                >
                  {textConfig.CREATION_USER_TEXT_CLOSE}
                </Button>
              </Box>
            </Box>
          </>
        )}

        {activeTab === "admin" && userData && (
          <ContentContainer>
            <AdminUser
              textConfig={textConfig}
              userData={userData}
              email={email}
              group={group}
              setEmail={setEmail}
              setGroup={setGroup}
              username={username}
              setUsername={setUsername}
              isLoading={isLoading}
              handleSearch={handleSearch}
            />
          </ContentContainer>
        )}

        {activeTab === "create" && (
          <ContentContainer>
            <CreationUser textConfig={textConfig} onClose={onClose} />
          </ContentContainer>
        )}
      </BaseModal>
      <CustomAlert
        open={alertConfig.open}
        type={alertConfig.type}
        message={alertConfig.message}
        title={alertConfig.title}
        onClose={handleCloseAlert}
      />
    </>
  );
}
